import classnames from 'classnames';

import { COLUMNS_MAP } from '@/utils/columns';
import { useMobileCarouselSettings } from '@/utils/useMobileCarouselSettings';
import type { CSSProperties } from 'react';
import EmblaCarousel from '../components/EmblaCarousel';
import * as styles from './Products.css';
import type { ProductsElementProps } from './Products.types';
import { ProductSerializer } from './Serializer';

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};

const handleDivider = (columns: string, horizontalScroll: boolean, hasDivider: boolean) => {
  try {
    if (!hasDivider) {
      return '';
    }

    return (columns && Number.parseInt(columns) > 1) || horizontalScroll
      ? styles.productsGridColumn.multipleColumns
      : styles.productsGridColumn.singleColumn;
  } catch {
    return '';
  }
};

function ProductsElement(props: ProductsElementProps) {
  const {
    element,
    children,
    attributes,
    className,
    isLive = false,
    paginateHasMorePosts,
    paginateFetchMorePosts,
    isLoading = false,
    onSearchQueryChange,
    isSearching,
    setIsSearching,
    handleNonDebouncedSearchQuery,
    handleResetSearchState,
    viewport,
    ...rest
  } = props;

  const {
    mobile_hide,
    desktop_hide,
    customAttributes,

    data,

    structureTiptapState,

    gap,
    mobileGap,
    padding,

    // Divider
    dividerColor,
    dividerStyle,
    dividerThickness,
  } = element.attrs || {};

  const isViewportMobile = viewport === 'mobile';

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({ attributes: element.attrs, isViewportMobile: isViewportMobile || false });

  const desktopSlideSize =
    displayedShouldScrollHorizontally && !!effectiveColumns ? `${100 / Number.parseInt(effectiveColumns)}%` : '';

  const { products } = data || [];

  const dividerClass = handleDivider(
    effectiveColumns || '3',
    !!displayedShouldScrollHorizontally,
    dividerStyle !== 'none',
  );

  return (
    <div
      {...rest}
      {...customAttributes}
      className="relative flex flex-col"
      style={
        {
          width: '100%',
          '--gap': gap,
          '--mobile-gap': mobileGap,
          '--products-padding': padding,

          // Slider
          '--products-desktop-slide-size': desktopSlideSize,
          '--products-slider-control-background-color': displayedSliderControlsBackgroundColor,
          '--products-slider-control-text-color': displayedSliderControlsTextColor,
          '--products-slider-control-border-color': displayedSliderControlsBorderColor,

          // Mobile Slider
          '--products-mobile-slider-control-background-color':
            element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
          '--products-mobile-slider-control-text-color':
            element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
          '--products-mobile-slider-control-border-color':
            element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,

          // Dividers
          '--products-divider-color': dividerColor,
          '--products-divider-style': dividerStyle,
          '--products-divider-thickness': dividerThickness,
        } as CSSProperties
      }
      {...attributes}
    >
      <div
        className={classnames(
          className,
          {
            'mobile:hidden': mobile_hide,
            'desktop:hidden': desktop_hide,
          },
          customAttributes?.className,
          'relative w-full',
          styles.productsGrid,
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive={isLive}
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {products?.map((product) => {
            return (product?.product_variants || []).map((productVariant) => (
              <div
                key={product.id}
                className={classnames(dividerClass, 'embla__slide relative', isLive && 'cursor-pointer')}
              >
                {isLive ? (
                  <a href={product.url}>
                    <ProductSerializer
                      node={structureTiptapState?.content?.[0] as any}
                      rootClassName={'h-full'}
                      rootStyle={{}}
                      product={product}
                      productVariant={productVariant}
                    />
                  </a>
                ) : (
                  <ProductSerializer
                    node={structureTiptapState?.content?.[0] as any}
                    rootClassName={'h-full'}
                    rootStyle={{}}
                    product={product}
                    productVariant={productVariant}
                  />
                )}
              </div>
            ));
          })}
        </EmblaCarousel>
      </div>

      {children}
    </div>
  );
}

export { ProductsElement };
